import React, { useState, useEffect, Suspense } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import PageTopLoader from '../components/PageTopLoader';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import {
  LayoutDashboard,
  Users,
  MapPin,
  Gift,
  FolderOpen,
  AlertTriangle,
  FileText,
  BarChart3,
  LogOut,
  Menu,
  X,
  ChevronRight,
  ChevronDown,
  User,
  Smartphone,
  Settings,
  Shield,
  Package,
  Truck,
  Building2,
  ShoppingCart,
  Warehouse,
  CalendarRange,
  BookOpen
} from 'lucide-react';
import { ROLE_LABELS } from '../lib/utils';

const navItems = [
  { path: '/dashboard', label: 'Tableau de bord', icon: LayoutDashboard },
  { path: '/dossiers', label: 'Dossiers de remise', icon: FolderOpen },
  { 
    label: 'Logistique', 
    icon: Package,
    submenu: [
      { path: '/planning', label: 'Planning tirages', icon: CalendarRange, badgeKey: 'planning_alertes' },
      { path: '/commandes', label: 'Commandes', icon: ShoppingCart, badgeKey: 'commandes_en_attente' },
      { path: '/stocks', label: 'Stocks', icon: Warehouse },
      { path: '/fournisseurs', label: 'Fournisseurs', icon: Building2 },
    ]
  },
  { 
    label: 'Configuration', 
    icon: Settings,
    adminOnly: true,
    submenu: [
      { path: '/sites', label: 'Sites de remise', icon: MapPin },
      { path: '/societes-livraison', label: 'Sociétés de livraison', icon: Truck },
      { path: '/users', label: 'Utilisateurs', icon: Users, adminOnly: true },
      { path: '/settings', label: 'Paramètres', icon: Settings, superAdminOnly: true },
      { path: '/permissions', label: 'Permissions', icon: Shield, superAdminOnly: true },
      { path: '/guide', label: "Guide d'utilisation", icon: BookOpen },
    ]
  },
  { path: '/anomalies', label: 'Anomalies', icon: AlertTriangle },
  { path: '/audit', label: 'Audit', icon: FileText },
  { path: '/reporting', label: 'Reporting', icon: BarChart3 },
];

const BackofficeLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openSubmenus, setOpenSubmenus] = useState(['Logistique', 'Configuration']);
  const [commandesEnAttente, setCommandesEnAttente] = useState(0);
  const [planningAlertes, setPlanningAlertes] = useState({ urgent: 0, depasse: 0, total: 0 });

  useEffect(() => {
    let cancelled = false;
    const fetchCounts = async () => {
      try {
        const [cmdRes, planRes] = await Promise.all([
          api.get('/commandes/stats/overview').catch(() => null),
          api.get('/tirages/stats/alertes').catch(() => null),
        ]);
        if (cancelled) return;
        if (cmdRes?.data) setCommandesEnAttente(cmdRes.data.en_attente || 0);
        if (planRes?.data) setPlanningAlertes(planRes.data);
      } catch (_) {
        // silencieux : la sidebar ne doit pas crasher si l'API est temporairement indisponible
      }
    };
    fetchCounts();
    const intervalId = setInterval(fetchCounts, 60000);
    // Refresh instantané sur événement custom déclenché par les pages après une mutation
    const handleRefresh = () => fetchCounts();
    window.addEventListener('badges:refresh', handleRefresh);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
      window.removeEventListener('badges:refresh', handleRefresh);
    };
  }, []);

  const badgeCounts = {
    commandes_en_attente: commandesEnAttente,
    planning_alertes: planningAlertes.total,
  };
  // Si une ligne en alerte est "depasse", on rend le badge planning rouge (plus critique)
  const planningBadgeIsCritical = planningAlertes.depasse > 0;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleSubmenu = (label) => {
    setOpenSubmenus(prev => 
      prev.includes(label) 
        ? prev.filter(l => l !== label)
        : [...prev, label]
    );
  };

  const shouldShowItem = (item) => {
    if (item.superAdminOnly) {
      return user?.role === 'admin_kadolo';
    }
    if (item.adminOnly) {
      return user?.role === 'admin_kadolo' || user?.role === 'operator_kadolo';
    }
    return true;
  };

  const filteredNavItems = navItems.filter(item => {
    if (item.submenu) {
      // Filter submenu items
      const visibleSubmenu = item.submenu.filter(sub => shouldShowItem(sub));
      return visibleSubmenu.length > 0 && shouldShowItem(item);
    }
    return shouldShowItem(item);
  });

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-slate-200 z-50 px-4 flex items-center justify-between">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(true)}
          data-testid="mobile-menu-btn"
        >
          <Menu className="h-5 w-5" />
        </Button>
        <h1 className="font-semibold text-indigo-700">KADOLOGISTIQUE</h1>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="user-menu-mobile">
              <User className="h-5 w-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>{user?.prenom} {user?.nom}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/mobile')} data-testid="switch-to-mobile">
              <Smartphone className="mr-2 h-4 w-4" />
              Vue mobile
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleLogout} data-testid="logout-mobile">
              <LogOut className="mr-2 h-4 w-4" />
              Déconnexion
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      {/* Sidebar overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-64 bg-white border-r border-slate-200 z-50
        transform transition-transform duration-200 ease-in-out
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="h-16 flex items-center justify-between px-6 border-b border-slate-200">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-indigo-700 rounded-lg flex items-center justify-center">
              <Gift className="h-5 w-5 text-white" />
            </div>
            <span className="font-bold text-xl text-indigo-700">KADOLOGISTIQUE</span>
          </Link>
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        <ScrollArea className="h-[calc(100vh-8rem)]">
          <nav className="p-4 space-y-1">
            {filteredNavItems.map((item) => {
              const Icon = item.icon;
              
              // Item avec sous-menu
              if (item.submenu) {
                const isOpen = openSubmenus.includes(item.label);
                const hasActiveChild = item.submenu.some(sub => location.pathname === sub.path);
                const visibleSubmenu = item.submenu.filter(sub => shouldShowItem(sub));
                
                return (
                  <Collapsible key={item.label} open={isOpen} onOpenChange={() => toggleSubmenu(item.label)}>
                    <CollapsibleTrigger asChild>
                      <button
                        className={`
                          w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200
                          ${hasActiveChild 
                            ? 'bg-indigo-50 text-indigo-700' 
                            : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                          }
                        `}
                      >
                        <Icon className="h-5 w-5 flex-shrink-0" />
                        <span className="text-sm font-medium">{item.label}</span>
                        <ChevronDown className={`h-4 w-4 ml-auto transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                      </button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="pl-4 mt-1 space-y-1">
                      {visibleSubmenu.map((subItem) => {
                        const isActive = location.pathname === subItem.path;
                        const SubIcon = subItem.icon;
                        const badgeValue = subItem.badgeKey ? badgeCounts[subItem.badgeKey] : 0;
                        const isPlanningCritical = subItem.badgeKey === 'planning_alertes' && planningBadgeIsCritical;
                        const isPlanningWarning = subItem.badgeKey === 'planning_alertes' && !planningBadgeIsCritical;
                        const badgeClass = isPlanningCritical
                          ? 'bg-red-600 text-white'
                          : isPlanningWarning
                          ? 'bg-red-200 text-red-900'
                          : 'bg-amber-200 text-amber-900';
                        const badgeTitle = subItem.badgeKey === 'planning_alertes'
                          ? `${planningAlertes.urgent} urgent(s)${planningAlertes.depasse ? ` • ${planningAlertes.depasse} dépassé(s)` : ''}`
                          : `${badgeValue} commande(s) en attente de validation`;

                        return (
                          <Link
                            key={subItem.path}
                            to={subItem.path}
                            onClick={() => setSidebarOpen(false)}
                            className={`
                              flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 text-sm
                              ${isActive 
                                ? 'bg-indigo-100 text-indigo-700 font-medium' 
                                : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                              }
                            `}
                          >
                            <SubIcon className="h-4 w-4 flex-shrink-0" />
                            <span>{subItem.label}</span>
                            {badgeValue > 0 && (
                              <span
                                data-testid={`nav-badge-${subItem.badgeKey}`}
                                title={badgeTitle}
                                className={`ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-md text-xs font-semibold shadow-sm ${badgeClass}`}
                              >
                                {badgeValue}
                              </span>
                            )}
                          </Link>
                        );
                      })}
                    </CollapsibleContent>
                  </Collapsible>
                );
              }
              
              // Item simple
              const isActive = location.pathname === item.path || 
                (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.path.replace('/', '')}`}
                  className={`
                    flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200
                    ${isActive 
                      ? 'bg-indigo-50 text-indigo-700 font-medium' 
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                    }
                  `}
                >
                  <Icon className="h-5 w-5 flex-shrink-0" />
                  <span className="text-sm">{item.label}</span>
                  {isActive && <ChevronRight className="h-4 w-4 ml-auto" />}
                </Link>
              );
            })}
          </nav>
        </ScrollArea>

        {/* User section at bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200 bg-white">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button 
                className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors"
                data-testid="user-menu-btn"
              >
                <div className="w-9 h-9 bg-indigo-100 rounded-full flex items-center justify-center">
                  <User className="h-5 w-5 text-indigo-700" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-slate-900">{user?.prenom} {user?.nom}</p>
                  <p className="text-xs text-slate-500">{ROLE_LABELS[user?.role] || user?.role}</p>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Mon compte</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/mobile')} data-testid="switch-mobile">
                <Smartphone className="mr-2 h-4 w-4" />
                Vue mobile
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                <LogOut className="mr-2 h-4 w-4" />
                Déconnexion
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        <div className="p-4 md:p-6 lg:p-8">
          <Suspense fallback={<PageTopLoader />}>
            {children}
          </Suspense>
        </div>
      </main>
    </div>
  );
};

export default BackofficeLayout;
